/** 後端 JWT payload（精簡版，只列前端會用到的 claim）。 */
export interface JwtPayloadInterface {
  exp?: number
  iat?: number
  expired_at?: string
  id?: string
  type?: string
  data?: Record<string, unknown>
}

/**
 * 解 JWT payload（中段 base64url decode → JSON parse）。
 * 不驗 signature（那是後端的事），純取資訊用。失敗一律回 null。
 */
export function parseJwtPayload(token: string | null | undefined): JwtPayloadInterface | null {
  if (!token) return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    const base64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = atob(base64)
    // 處理 UTF-8 multi-byte（payload 可能含中文，如 data.name）
    const json = decodeURIComponent(
      decoded
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )
    return JSON.parse(json) as JwtPayloadInterface
  }
  catch {
    return null
  }
}

/**
 * 回傳 token 距離過期還剩幾秒。token 無效 / 無 exp → 回 -1（呼叫端視為「不該排 timer」）。
 * 已過期 → 回負數。
 *
 * @param nowMs 測試用注入點，預設 Date.now()
 */
export function getTokenSurplusSec(token: string | null | undefined, nowMs: number = Date.now()): number {
  const payload = parseJwtPayload(token)
  if (!payload?.exp) return -1
  const nowSec = Math.floor(nowMs / 1000)
  return payload.exp - nowSec
}

const RENEW_WINDOW_MIN_SEC = 5 * 60 // 過期前 5 min
const RENEW_WINDOW_MAX_SEC = 15 * 60 // 過期前 15 min
const MAX_SCHEDULE_SEC = 86400 // 剩 > 1 day 不排 timer（reload 時會再評估）

/**
 * 決定下次 renew 該在「多久之後」觸發（毫秒）。
 *
 * 回傳值約定：
 *  -  `0`：立即 renew（surplus 已過期 / 在 window 內）
 *  - `> 0`：setTimeout 的 delay 毫秒
 *  -  `-1`：不排 timer（surplus 太長，reload 時會再評估）
 *
 * @param surplusSec 剩餘秒數
 * @param random     測試用注入點，預設 Math.random
 */
export function pickRenewDelayMs(
  surplusSec: number,
  random: () => number = Math.random,
): number {
  if (surplusSec <= 0) return 0
  if (surplusSec < RENEW_WINDOW_MAX_SEC) return 0
  if (surplusSec > MAX_SCHEDULE_SEC) return -1
  const windowRange = RENEW_WINDOW_MAX_SEC - RENEW_WINDOW_MIN_SEC
  const windowSec = RENEW_WINDOW_MIN_SEC + Math.floor(random() * windowRange)
  return (surplusSec - windowSec) * 1000
}
